import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { NavigationItem } from '@shared/models/navigation-item';
import { CurrentUserService, HeaderService } from '@shared/services';
import { ShadowLayerService } from '@shared/services/shadow-layer.service';
import { SidebarItemListService } from '@shared/services/sidebar-item.service';
import { filter, map, timer } from 'rxjs';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';
import { InvestmentJurisdictionService } from 'src/app/modules/admin/services';
import { NavigationComponent } from '../navigation/navigation.component';

@Component({
  standalone: false,
  selector: 'gstar-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly headerService = inject(HeaderService);
  private readonly sidebarItemService = inject(SidebarItemListService);
  private readonly shadowLayerService = inject(ShadowLayerService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly destroyRef = inject(DestroyRef);

  solid = true;
  collapsed = false;
  selectedIndex: WritableSignal<number> = signal(-1);
  selectedSubIndex: WritableSignal<number> = signal(-1);
  selectedNestedIndex: WritableSignal<number> = signal(-1);
  hasFooter: boolean = true;
  selectedCountry: WritableSignal<
    InvestmentJurisdictionShort | null | undefined
  > = signal(null);
  lastItemClicked: WritableSignal<NavigationItem> = signal(
    {} as NavigationItem
  );
  url: WritableSignal<string> = signal('');

  navList: WritableSignal<NavigationItem[]> = signal(
    this.sidebarItemService.getNavList(
      this.currentUserService.isAdmin(),
      this.currentUserService.isCountryAdmin(),
      this.selectedCountry() ?? null
    )
  );
  navElement = viewChild<NavigationComponent>('nav');

  constructor() {
    effect(() => {
      this.navList.set(
        this.sidebarItemService.getNavList(
          this.currentUserService.isAdmin(),
          this.currentUserService.isCountryAdmin(),
          this.selectedCountry() ?? null
        )
      );
    });
  }

  get isCountryDisabled(): boolean {
    return (
      this.url().toLowerCase().includes('country') &&
      !((this.selectedCountry()?.investmentJurisdictionId ?? -1) >= 0)
    );
  }

  ngOnInit(): void {
    this.headerService.selectedClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.syncSelectedCountryFromUrl(this.url() || this.router.url);
      });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        map((navigationEnd: NavigationEnd) => navigationEnd.url)
      )
      .subscribe((url: string) => {
        this.url.set(url);
        this.syncSelectedCountryFromUrl(url);
        this.selectIndex(url);
        this.checkCountryShadow();
      });

    this.url.set(this.router.url);
    this.syncSelectedCountryFromUrl(this.router.url);
    this.selectIndex(this.router.url);
    this.checkCountryShadow();
  }

  redirect(item: NavigationItem): void {
    timer(0).subscribe(() => {
      this.lastItemClicked.set(item);
      if (this.hasCountryId(item)) {
        const replacedLink = item.routerLink?.replace(
          '{countryId}',
          this.selectedCountry()?.country?.countryId?.toString() ?? ''
        );
        this.router.navigate([replacedLink]);
      } else {
        this.router.navigate([item.routerLink]);
      }
    });
  }

  private selectIndex(url: string): void {
    let index: number = -1;
    let subIndex: number = -1;
    let nestedIndex: number = -1;
    index = this.getIndex(this.navList(), url, 1);
    if (index >= 0) {
      this.setIndexes(index, subIndex, nestedIndex);
    } else {
      const subChildren =
        this.navList()?.filter((item: NavigationItem) => item.children)[0]
          .children ?? [];
      if (subChildren.length > 0) {
        subIndex = this.getIndex(subChildren, url, 3);
        if (subIndex >= 0) {
          this.setIndexes(index, subIndex, nestedIndex);
        } else {
          const nestedChildren =
            subChildren.filter((subItem: NavigationItem) => subItem.children)[0]
              .children ?? [];
          if (nestedChildren.length > 0) {
            nestedIndex = this.getIndex(nestedChildren, url, 4);
          }
          this.setIndexes(index, subIndex, nestedIndex);
        }
      }
    }
  }

  setIndexes(index: number, subIndex: number, nestedIndex: number): void {
    this.selectedIndex.set(index);
    this.selectedSubIndex.set(subIndex);
    this.selectedNestedIndex.set(nestedIndex);
  }

  getIndex(items: NavigationItem[], url: string, urlItem: number): number {
    return (
      items.findIndex(
        (nestedItem: NavigationItem) =>
          nestedItem.routerLink &&
          `/${url.split('/')[urlItem]}`.includes(
            nestedItem.routerLink.split('/')[urlItem] ?? ''
          )
      ) ?? -1
    );
  }

  selectCountryHandler(
    country: InvestmentJurisdictionShort | null | undefined
  ): void {
    if (country?.investmentJurisdictionId) {
      this.selectedCountry.set(country);
      if (this.hasCountryId(this.lastItemClicked())) {
        this.redirect(this.lastItemClicked());
      }
    } else {
      this.selectedCountry.set(null);
    }
    this.navList.set(
      this.sidebarItemService.getNavList(
        this.currentUserService.isAdmin(),
        this.currentUserService.isCountryAdmin(),
        this.selectedCountry() ?? null
      )
    );
    this.currentUserService.loadCountryAdminPermissions(true);
    this.sidebarItemService.hasCountryChanged.set(true);
    this.checkCountryShadow();
  }

  hasCountryId(item: NavigationItem): boolean {
    return item.routerLink?.includes('{countryId}') ?? false;
  }

  checkCountryShadow(): void {
    this.shadowLayerService.showShadowLayer.next(this.isCountryDisabled);
  }

  /**
   * If refreshing the page, the selected country is not set.
   * This method will check if the url has a countryId and set the selected country on sidebar accordingly.
   * @param url: string - the current url
   */
  private syncSelectedCountryFromUrl(url: string): void {
    const countryId = this.getCountryIdFromUrl(url);

    if (!countryId) {
      return;
    }

    if (this.selectedCountry()?.country?.countryId === countryId) {
      return;
    }

    this.investmentJurisdictionService
      .getInvestmentJurisdictionsForNav()
      .subscribe(response => {
        const matchedCountry =
          response.items.find(
            country => country.country?.countryId === countryId
          ) ?? null;

        this.selectedCountry.set(matchedCountry);
        this.navList.set(
          this.sidebarItemService.getNavList(
            this.currentUserService.isAdmin(),
            this.currentUserService.isCountryAdmin(),
            this.selectedCountry() ?? null
          )
        );
        this.checkCountryShadow();
      });
  }

  private getCountryIdFromUrl(url: string): number | undefined {
    const match = url.split('?')[0].match(/^\/country\/(\d+)(?:\/|$)/);

    return match ? parseInt(match[1], 10) : undefined;
  }
}
